import type { User } from '@creator/sdk/modules/account/account.model';
import Button from '@creator/ui/components/Button/ButtonV2';
import RadioButton from '@creator/ui/components/RadioButton/RadioButton';
import Text from '@creator/ui/components/Text/Text';
import UserDisplayName from '@creator/ui/components/UserDisplayName/UserDisplayName';
import AlertBox from '@src/basic-components/AlertBox/AlertBox';
import ProfileImage from '@src/components/ProfileImage/ProfileImage';
import { useStoreActions } from '@src/model/hooks';
import creatorSdk from '@src/services/creator-sdk';
import { noop } from '@src/utils/utils';
import { Fragment, useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

export interface MergeAccountProps {
    className?: string;
    users: User[];
    initialSelectedUser?: User;
    cancelButtonText?: string;
    onSuccess: (user: User) => void;
    onUserClick: (user: User) => void;
    onCancelClick: () => void;
}

const MergeAccount: FC<MergeAccountProps> = props => {
    const { cancelButtonText, initialSelectedUser, onSuccess, onCancelClick, users, className } = props;

    const [selectedUser, setSelectedUser] = useState(initialSelectedUser || users[0]);
    const [isMerging, setIsMerging] = useState(false);
    const [step, setStep] = useState<'form' | 'confirmation'>('form');
    const forwardAccount = useStoreActions(actions => actions.user.forwardAccount);

    const { t } = useTranslation();

    function isUserSelected(userId: string): boolean {
        return selectedUser.id === userId;
    }

    function mapError(error) {
        // if (error.code ===  'auth/credential-already-in-use') return setError(error.code);
        alert(`${t('generalErrorMessage')} ${error.message}`);
    }

    function mapFailMergeUsers(forwardedAccounts: Record<string, string>): void {
        // if (error.code ===  'auth/credential-already-in-use') return setError(error.code);
        const failedForwardedAccountsKeys = Object.keys(forwardedAccounts).filter(key => forwardedAccounts[key].includes('Fail -'));
        if (!failedForwardedAccountsKeys.length) return;

        alert(
            `${t('mergeAccount.failedForwardedAccounts')} ${failedForwardedAccountsKeys.map(key => `${users.find(user => user.id === key)?.displayName} - ${forwardedAccounts[key]}, `)}`,
        );
    }

    function onUserClick(user: User): void {
        const { onUserClick = noop } = props;
        setSelectedUser(user);
        onUserClick(user);
    }

    async function onMergeAccountsSubmit(): Promise<void> {
        setIsMerging(true);
        try {
            const userIds = users.filter(user => !user.forwardTo && !user.mainAccount && user.id !== selectedUser.id).map(user => user.id);

            const res = await creatorSdk.accountModule.forwardAccount({
                userIds,
                forwardToId: selectedUser.id,
            });

            setIsMerging(false);
            mapFailMergeUsers(res.forwardedAccounts);
            if (!res.mainAccount) return;

            onSuccess({ ...res.mainAccount, id: res.mainAccount.blockchainId });
        } catch (error) {
            console.error(error);
            setIsMerging(false);
            mapError(error);
        }
    }

    function renderOption(user: User) {
        return (
            <div key={user.id} className="flex items-center cursor-pointer" onClick={() => onUserClick(user)}>
                <RadioButton className="mr-3" isActive={isUserSelected(user.id)} />
                <ProfileImage className="mr-3 w-7 h-7" displayName={user.displayName} />
                <UserDisplayName textProps={{ className: 'font-bold' }} displayName={user.displayName} />
            </div>
        );
    }

    function renderPrimaryAccountOption(displayName: string) {
        return (
            <div className="flex items-center justify-center mt-5 cursor-pointer">
                <ProfileImage className="mr-5 w-7 h-7" displayName={displayName} />
                <UserDisplayName textProps={{ className: 'font-bold' }} displayName={displayName} />
            </div>
        );
    }

    function renderOptions() {
        if (initialSelectedUser) return renderPrimaryAccountOption(initialSelectedUser.displayName);

        return users.map(renderOption);
    }

    function renderDescription() {
        return (
            <Text enableMultiline={true} className="block mt-5 text-sm font-medium text-gray-500 dark:text-gray-500">
                {t('mergeAccount.description')}
            </Text>
        );
    }

    function renderAlertBox() {
        return (
            <AlertBox className="mt-3" type="error">
                {t('mergeAccount.aletBox')}
            </AlertBox>
        );
    }

    function renderSubtitle() {
        const text = initialSelectedUser ? t('mergeAccount.subtitleIntialSelectedUser') : t('mergeAccount.subtitle');
        return (
            <Text enableMultiline={true} className="block -mt-2 font-medium text-center text-gray-500">
                {text}
            </Text>
        );
    }

    function renderForm() {
        return (
            <Fragment>
                {renderSubtitle()}
                <div className="mt-5 space-y-5">{renderOptions()}</div>
                {renderDescription()}
                {renderAlertBox()}
                <div className="flex justify-center space-x-5 mt-7">
                    <Button size="sm" type="normal" className="w-40" onClick={() => onCancelClick()}>
                        <Text>{cancelButtonText || t('mergeAccount.cancel')}</Text>
                    </Button>
                    <Button size="sm" type="primary" className="w-40" onClick={() => setStep('confirmation')}>
                        <Text>{t('mergeAccount.submitButton')}</Text>
                    </Button>
                </div>
            </Fragment>
        );
    }

    function renderConfirmation() {
        return (
            <Fragment>
                <Text enableMultiline={true} className="block -mt-2 font-medium text-center text-gray-500">
                    {t('mergeAccount.confirmation.subtitle')}
                </Text>
                {renderPrimaryAccountOption(selectedUser.displayName)}
                <div className="flex justify-center space-x-5 mt-7">
                    <Button size="sm" color="base" className="w-40" onClick={() => setStep('form')}>
                        <Text>{t('mergeAccount.confirmation.back')}</Text>
                    </Button>
                    <Button isDisabled={!selectedUser} isLoading={isMerging} size="sm" className="w-40" onClick={() => onMergeAccountsSubmit()}>
                        <Text>{t('mergeAccount.confirmation.submitButton')}</Text>
                    </Button>
                </div>
            </Fragment>
        );
    }

    function renderContent() {
        if (step === 'form') return renderForm();
        return renderConfirmation();
    }

    return <div className={`${className}`}>{renderContent()}</div>;
};

export default MergeAccount;
